/**
 * @ngdoc service
 * @name AriaService
 * @module mx.app
 *
 * @description
 * Service provides methods to work with ARIA
 */
import type { I18n } from '../../i18n/mx-internationalization';
import type { IDeferred, IPromise, IQService, IWindowService } from 'angular';

export type AriaMessage = {
	message: string;
	delay: number;
	resultDefer: IDeferred<unknown>;
};

export class AriaService {
	public static $inject = ['mx.internationalization', '$q', '$window'];
	public ariaMessageQueue: Array<AriaMessage> = [];
	private readingAriaInProgress = false;

	constructor(
		private readonly i18n: I18n,
		private readonly $q: IQService,
		private readonly $window: IWindowService
	) {}

	/**
	 * @ngdoc method
	 * @name AriaService#setAppInitialFocus
	 *
	 * @description
	 * set initial focus for App
	 */
	setAppInitialFocus(): void {
		setTimeout(function () {
			$('.mx-shell-toolbar__title').focus();
		}, 10);
	}

	/**
	 * @ngdoc method
	 * @name AriaService#handleAppSwitchingMessage
	 *
	 * @description
	 * Inform blind user about Switching Application
	 *
	 * @param {object} currentApp - current Application
	 */
	handleAppSwitchingMessage(currentApp: { title: string }): void {
		if (this.getAppName(document.referrer) !== this.getAppName(this.$window.location.href)) {
			this.readMessage((this.i18n.get('shell.aria.application-switch') + currentApp.title).replace(/:/g, ' '), 4000);
		}
	}

	private getAppName(url: string): string {
		return ((url || '').match(/\/(app-[^/]+)/) || [])[1];
	}

	/**
	 * @ngdoc method
	 * @name AriaService#readMessage
	 *
	 * @description
	 * Inform blind user some message
	 *
	 * @param {string} message - message to be read
	 * @param {number} delay - time, reserved for reading current message (default is `3000` which is 3 seconds)
	 *
	 * @returns {promise} - resolves when message was read
	 */
	readMessage(message: string, delay = 3000): IPromise<unknown> {
		const resultDefer = this.$q.defer();
		this.ariaMessageQueue.push({
			message: message,
			delay: delay,
			resultDefer: resultDefer,
		});
		this.readNextMessage();
		return resultDefer.promise;
	}

	private readNextMessage(): void {
		if (this.ariaMessageQueue.length && !this.readingAriaInProgress) {
			this.readingAriaInProgress = true;
			const msgObj = this.ariaMessageQueue.shift();
			if (!msgObj) {
				return;
			}
			const msgEl = $('<div class="mx-aria-only accessible-alert" role="alert" tabindex="-1"></div>');
			msgEl.text(msgObj.message);
			msgEl.appendTo('body');
			// For Debugging: console.log('%cARIA Alert: ' + msgObj.message, 'color: DodgerBlue');
			setTimeout(() => {
				msgEl.remove();
				this.readingAriaInProgress = false;
				msgObj.resultDefer.resolve();
				if (this.ariaMessageQueue.length) {
					this.readNextMessage();
				}
			}, msgObj.delay);
		}
	}

	/**
	 * @ngdoc method
	 * @name AriaService#createPoliteFocusSettingFn
	 *
	 * @description
	 * This method creates function, which remember focused element when function was created
	 * and will set focus to element by given cssSelector when this returned fucntion will be called.
	 * Focus will be moved just if user didn't change it manually.
	 * It can be useful if we need to read ARIA message and set focus after that
	 * in a case if user hasn't focused another element by himself.
	 *
	 * @param {string} cssSelector - css selector of element to be focused
	 *
	 * @returns {promise} - resolves when message was read
	 */
	createPoliteFocusSettingFn(cssSelector: string) {
		let isActual = true;
		const initialActiveElement = document.activeElement;
		function hendleKeyDown(event: JQueryKeyEventObject) {
			if (document.activeElement?.tagName === 'INPUT' && event.which !== 13) {
				isActual = false;
				$(document.body).off('keydown', hendleKeyDown);
			}
			return undefined;
		}
		$(document.body).on('keydown', hendleKeyDown);
		return () => {
			setTimeout(() => {
				const activeEl = document.activeElement;
				if (isActual && (activeEl === initialActiveElement || activeEl === document.body)) {
					$(cssSelector).trigger('focus');
				}
			}, 100);
		};
	}
}
